import React, { useContext, useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Grid,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import { FormServiceContext } from 'app/utils/contexts';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedVehicles, clearSelectedVehicles } from 'app/store/slices/vehicle';
import { Field } from 'formik';
import _ from 'lodash';
import helpers from 'app/utils/helpers';
import { validatePassengersByVehicle } from 'app/utils/forms/validators/license';
import { STATUS_CODES } from 'app/config/status';
import { DOCUMENT_TYPES } from 'app/utils/constants';
import filesEndpoint from 'app/api/services/filesEndpoint';

function VehiclesSearch(props) {
  const { errors, values, touched, setFieldValue, classes } = props;

  const dispatch = useDispatch();

  const inspectedVehicles = useSelector(state => state.vehicle.inspectedVehicles);
  const vehicleTypes = useSelector(state => state.vehicle.vehicleType);
  const vehiclesCategories = useSelector(state => state.vehicle.vehicleCategories);
  const request = useSelector(state => state.requests.details);

  const [vehicle, setVehicle] = useState({});
  const [loading, setLoading] = useState(false);
  const [vehicleList, setVehicleList] = useState([]);

  const { canEdit, isInternalForm, setLoadingDocuments } = useContext(FormServiceContext);

  const handleVehicleData = useCallback(
    (dePlaca, fromInit, tempVehicleList) => {
      let vehicle = _.find(vehicleList, ['dePlaca', dePlaca]);
      if (fromInit) {
        vehicle = _.find(tempVehicleList, ['dePlaca', dePlaca]);
      }
      if (!vehicle) return;

      const tempVehicle = _.cloneDeep(vehicle);
      tempVehicle.documentos = [];

      const vehicleType = _.find(vehicleTypes, ['cdTipoVeiculo', vehicle.cdTipoVeiculo]);
      const vehicleCategory = _.find(vehiclesCategories, [
        'cdCategoriaVeiculo',
        vehicle.cdCategoriaVeiculo,
      ]);
      setVehicle({
        ...tempVehicle,
        formattedCpfCnpjProprietario: helpers.formatarDocumento(vehicle.deCpfCnpjProprietario),
        deTipoVeiculo: vehicleType?.deTipoVeiculo || '',
        deCategoriaVeiculo: vehicleCategory?.deCategoriaVeiculo || '',
      });
      const formattedVehicleData = {
        cdVeiculo: vehicle.cdVeiculo,
        veiculo: { ...tempVehicle },
      };

      const licencaVeiculos = values.licencaVeiculos.filter(
        lv => lv.cdVeiculo === vehicle.cdVeiculo
      );

      let oldVehicles = [];
      if(licencaVeiculos.length === 0 && values.licencaVeiculos.length > 0){
        oldVehicles = values.licencaVeiculos.map(v => {
          const temp = {...v};
          temp.boExcluido = true;
          return temp;
        });
      }

      if (licencaVeiculos[0]) {
        if (licencaVeiculos[0].cdLicencaVeiculo)
          formattedVehicleData.cdLicencaVeiculo = licencaVeiculos[0].cdLicencaVeiculo;
        if (licencaVeiculos[0].cdLicenca)
          formattedVehicleData.cdLicenca = licencaVeiculos[0].cdLicenca;
      }

      setFieldValue('licencaVeiculos', [formattedVehicleData, ...oldVehicles]);
      dispatch(setSelectedVehicles([formattedVehicleData]));
      if (!fromInit) {
        validatePassengersByVehicle([formattedVehicleData], values.passageiros);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, setFieldValue, values.passageiros, vehicleTypes, vehicleList, vehiclesCategories]
  );

  useEffect(() => {
    if (values.licencaVeiculos && values.licencaVeiculos.length) {
      let vehicle = values.licencaVeiculos[0].veiculo;
      let tempVehicleList = inspectedVehicles;
      if (isInternalForm || request.cdSituacao === STATUS_CODES.APROVADO) {
        tempVehicleList = [vehicle];
      }
      setVehicleList(tempVehicleList);
      if (!vehicle) {
        vehicle = _.find(inspectedVehicles, ['cdVeiculo', values.licencaVeiculos[0].cdVeiculo]);
      }
      const { dePlaca } = vehicle;
      setFieldValue('dePlaca', dePlaca);
      handleVehicleData(dePlaca, true, tempVehicleList);
    } else {
      setVehicleList(inspectedVehicles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectedVehicles, isInternalForm, request]);

  const handleVehicleChange = async e => {
    const { value: dePlaca } = e.target;
    setFieldValue('dePlaca', dePlaca);
    if (!dePlaca) {
      setVehicle({});
      dispatch(clearSelectedVehicles());
      return;
    }

    setLoading(true);
    setLoadingDocuments(true);
    const found = _.find(inspectedVehicles, ['dePlaca', dePlaca]);

    const vehi = _.cloneDeep(found);
    if (vehi.documentos) {
      const actualDocuments = !values.documentos
        ? []
        : values.documentos.filter(
            doc =>
              doc.cdTipoDocumento !== DOCUMENT_TYPES.CRLV &&
              doc.cdTipoDocumento !== DOCUMENT_TYPES.CERTIFICADO_VISTORIA
          );

      vehi.documentos = vehi.documentos.filter(
        doc =>
          doc.cdTipoDocumento === DOCUMENT_TYPES.CRLV ||
          doc.cdTipoDocumento === DOCUMENT_TYPES.CERTIFICADO_VISTORIA
      );
      const tempDoc = [];

      for (const doc of vehi.documentos) {
        const temp = {};

        if (doc.cdTipoDocumento) temp.cdTipoDocumento = doc.cdTipoDocumento;
        if (doc.idFile) temp.idFile = doc.idFile;
        if (doc.boAlterado) temp.boAlterado = true;
        if (doc.boExcluido) temp.boExcluido = false;
        if (doc.fileName) temp.fileName = doc.fileName;

        if (!doc.fileName && doc.idFile) {
          const response = await filesEndpoint.getFileData(doc.idFile);
          if (response.status === 200 && response.data) {
            temp.fileName = response.data.name;
          }
        }
        const found = _.find(tempDoc, ['cdTipoDocumento', temp.cdTipoDocumento]);
        if (!found) tempDoc.push(temp);
      }
      setFieldValue('documentos', [...tempDoc, ...actualDocuments]);
    }
    setLoading(false);
    setLoadingDocuments(false);
    handleVehicleData(dePlaca);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <h3 className={classes.title}>Veículo</h3>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl
          variant="filled"
          margin="dense"
          fullWidth
          disabled={!canEdit}
          error={!!errors.dePlaca && canEdit && touched.dePlaca}
        >
          <InputLabel htmlFor="dePlaca" shrink={true}>
            Placa
          </InputLabel>
          <Field
            name="dePlaca"
            id="dePlaca"
            native
            disabled={!canEdit || isInternalForm}
            as={Select}
            onChange={e => handleVehicleChange(e)}
            validate={value => {
              if (!value) {
                return 'Campo obrigatório';
              }
            }}
          >
            <option aria-label="None" value="">
              Selecione
            </option>
            {vehicleList.map((type, index) => (
              <option key={`type-${index}`} value={type.dePlaca}>
                {type.dePlaca}
              </option>
            ))}
          </Field>
          {errors.dePlaca && canEdit && touched.dePlaca ? (
            <FormHelperText>{errors.dePlaca}</FormHelperText>
          ) : null}
        </FormControl>
      </Grid>

      {loading && (
        <Grid item xs={12} sm={6}>
          <CircularProgress size={16} style={{ marginTop: '25px' }} />
        </Grid>
      )}
      {!loading && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              name="deRenavam"
              label="Renavam"
              margin="dense"
              variant="filled"
              fullWidth
              disabled
              value={vehicle.deRenavam || ''}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="deTipoVeiculo"
              label="Tipo de veículo"
              margin="dense"
              variant="filled"
              fullWidth
              disabled
              value={vehicle.deTipoVeiculo || ''}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              name="qtCapacidade"
              label="Capacidade"
              margin="dense"
              variant="filled"
              fullWidth
              disabled
              value={vehicle.qtCapacidade || ''}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="deCategoriaVeiculo"
              label="Categoria"
              margin="dense"
              variant="filled"
              fullWidth
              disabled
              value={vehicle.deCategoriaVeiculo || ''}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="nmProprietario"
              label="Nome do proprietário"
              margin="dense"
              variant="filled"
              fullWidth
              disabled
              value={vehicle.nmProprietario || ''}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="deCpfCnpjProprietario"
              label="CNPJ/CPF"
              margin="dense"
              variant="filled"
              fullWidth
              disabled
              value={vehicle.formattedCpfCnpjProprietario || ''}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}

export default VehiclesSearch;
